import * as React from 'react';
import { FieldArray } from 'react-final-form-arrays';
import localeService from 'src/dataServices/LocaleService';
import { IRegistrationIdentifier } from 'src/state/nonprofit/types';
import RegistrationIdentifier, { IRegulatoryBodyExt } from './RegistrationIdentifier';
import { useRegistrationIdentifiers } from 'src/hooks/useRegistrationIdentifiers';

export interface IProfileLegalIdentifierProps {
  profileForm: any;
  registrationIdentifiers: IRegistrationIdentifier[];
  countryCode: string;
  enableMultiRegIdSelection?: boolean;
  organizationType: string;
}

export interface IRegulatoryBodiesProps {
  fields: any;
  countryCode: string;
  extRegulatoryBodies: Map<string, IRegulatoryBodyExt>;
}

const RegistrationIdentifiersEdit = ({
  profileForm,
  countryCode,
  registrationIdentifiers,
  enableMultiRegIdSelection,
  organizationType
}: IProfileLegalIdentifierProps): JSX.Element => {
  const extRegulatoryBodies = useRegistrationIdentifiers(countryCode, organizationType);
  const [deleteMessage, setDeleteMessage] = React.useState('');

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }}
      >
        {deleteMessage}
      </div>
      <FieldArray name="registrationIdentifiers" initialValue={registrationIdentifiers}>
        {({ fields }) => (
          <RenderIdentifiers
            fields={fields}
            props={{ countryCode, extRegulatoryBodies, registrationIdentifiers, profileForm, setDeleteMessage, enableMultiRegIdSelection }}
          />
        )}
      </FieldArray>
    </>
  );
};

interface IrenderJobRolesDropdown {
  fields: any;
  props: {
    countryCode: string;
    extRegulatoryBodies: any;
    registrationIdentifiers: any;
    profileForm: any;
    setDeleteMessage: (msg: string) => void;
    enableMultiRegIdSelection?: boolean;
  };
}

const RenderIdentifiers = (props: IrenderJobRolesDropdown) => {
  const {
    fields,
    props: { countryCode, extRegulatoryBodies, registrationIdentifiers, profileForm, setDeleteMessage, enableMultiRegIdSelection }
  } = props;
  const numRegulatoryBodies: number = extRegulatoryBodies ? extRegulatoryBodies.size : 0;

  if (fields.length === 0) {
    fields.update(0, undefined);
  }

  const handleAddRegistrationInfo = (fields: any) => {
    fields.push(undefined);
    setTimeout(() => {
      const allSelects = document.querySelectorAll('select[aria-label="Select registration type"]');
      const lastSelect = allSelects[allSelects.length - 1] as HTMLSelectElement | null;
      if (lastSelect) {
        lastSelect.focus();
      }
    }, 0);
  };

  return (
    <>
      {fields &&
        fields.map((field: any, index: number) => (
          <RegistrationIdentifier
            key={index}
            field={field}
            index={index}
            fields={fields}
            profileForm={profileForm}
            countryCode={countryCode}
            extRegulatoryBodies={extRegulatoryBodies}
            registrationIdentifiers={registrationIdentifiers}
            required={true}
            setDeleteMessage={setDeleteMessage}
          />
        ))}
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl12">
          {enableMultiRegIdSelection && regulatoryBodiesAddition(fields, numRegulatoryBodies) && (
            <button
              className="action-trigger glyph-prepend glyph-prepend-circle-plus mt-2"
              type="button"
              aria-label={localeService.getCategoryContent('Registration').AddRegistrationInformation}
              onKeyDown={(evt: { key: string }) => {
                if (evt.key === 'Enter') {
                  handleAddRegistrationInfo(fields);
                }
              }}
              onClick={() => {
                handleAddRegistrationInfo(fields);
              }}
            >
              {localeService.getText('ProfileV2', 'AddRegistartionInformation')}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

const regulatoryBodiesAddition = (fields: any, numRegulatoryBodies: number) => {
  return fields.length < numRegulatoryBodies;
};

export default RegistrationIdentifiersEdit;
